/**
 * Artwork for the store detail page.
 *
 * Every store now carries real photographs of itself, pulled from its Google
 * listing into `Location.imageUrl` and `Location.photosJson`, so those come
 * first. The bundled art below stays as the fallback for a store that has not
 * been synced yet.
 *
 * The bundled map was the only source before, and it had no entry for
 * Carrington, so that page quietly showed a photo of the Ogden store instead.
 */

/** Static storefront art (client/public/media/stores), keyed by covaLocationId. */
export const STORE_HERO_IMAGE_BY_COVA_ID: Record<number, string> = {
  265375: '/media/stores/ogden.webp',
  374740: '/media/stores/dover.webp',
  374742: '/media/stores/parkdale.webp',
  374743: '/media/stores/ranchlands.webp',
  374744: '/media/stores/okotoks.webp',
  374745: '/media/stores/southland.webp',
  387159: '/media/stores/coach-hill.webp',
};

const FALLBACK_STORE_HERO = '/media/stores/ogden.webp';

/**
 * Bring an API-absolute upload back to a path on this origin.
 *
 * The store API runs imageUrl through getStoreImageUrl, which prefixes the
 * browser-facing API origin. That is unreachable from inside the client
 * container, where next/image does its work, so optimizing one 500s with
 * "fetch failed" and the hero renders empty. next.config rewrites /uploads to
 * the backend over the internal network for this exact reason, so the relative
 * path is the one that works in both places. Anything not an upload, an
 * external CDN or a /media asset, is left alone.
 */
export function toLocalUploadPath(url: string): string {
  const trimmed = (url || '').trim();
  if (!trimmed) return '';
  const uploads = trimmed.indexOf('/uploads/');
  if (uploads > 0 && /^https?:\/\//i.test(trimmed)) return trimmed.slice(uploads);
  return trimmed;
}

type StoreLike = {
  covaLocationId: number;
  imageUrl?: string | null;
  photos?: string[] | null;
};

export function getStoreHeroImageSrc(covaLocationId: number, imageUrl?: string | null): string {
  const real = toLocalUploadPath(imageUrl || '');
  if (real) return real;
  return STORE_HERO_IMAGE_BY_COVA_ID[covaLocationId] ?? FALLBACK_STORE_HERO;
}

/**
 * Photos for a gallery, longest list first.
 *
 * Google mixes customer uploads in with the business's own photos, and the
 * business ones come back first, so taking from the front rather than shuffling
 * keeps the storefront and interior shots and leaves the random phone snaps at
 * the end.
 */
export function getStoreGallery(store: StoreLike, count: number): string[] {
  const primary = toLocalUploadPath(store.imageUrl || '');
  // Without the main shot. It is already the page hero, so including it here
  // put the same picture on screen twice. Falls back to it only when it is the
  // one photo the store has.
  const photos = (store.photos || [])
    .map(toLocalUploadPath)
    .filter((url) => Boolean(url) && url !== primary);
  const pool = photos.length > 0 ? photos : primary ? [primary] : [];

  if (pool.length === 0) {
    const bundled = STORE_HERO_IMAGE_BY_COVA_ID[store.covaLocationId] ?? FALLBACK_STORE_HERO;
    return Array.from({ length: count }, () => bundled);
  }

  // Repeat rather than leave holes when a store has fewer photos than slots.
  return Array.from({ length: count }, (_, i) => pool[i % pool.length]);
}
